import React from 'react'
import { Section, GuideHeader, P, UI, Steps, Bullets, Callout } from './GuideParts'

export default function IntegrationsGuide() {
  return (
    <div className="space-y-8">
      <GuideHeader
        title="Business Central and NetSuite"
        intro="Post approved requests straight into your ERP as purchase orders, vendor bills and similar records."
      />

      <Section number={1} title="Posting to Business Central or NetSuite">
        <P>
          These integrations are switched on per deployment, so you only see them if your organisation uses them.
          A template with an ERP record type adds a <UI>Business Central</UI> or <UI>NetSuite</UI> tab to the template.
          After a person approves a request, the data is posted as one ERP record.
        </P>
        <Bullets>
          <li>Each request produces <span className="font-semibold">one</span> record. A request that is already posted cannot be posted again.</li>
          <li>Activity has filters for Business Central and NetSuite so you can follow what was posted.</li>
        </Bullets>
      </Section>

      <Section number={2} title="Setting Up a Template">
        <Steps>
          <li>Create a template and choose the ERP record type (for example purchase order).</li>
          <li>Upload a sample PDF. The AI suggests the fields, writes a hint for each one (where it is printed, what to ignore) and a line-items table.</li>
          <li>Map each field to the matching ERP field. Line-item columns map to the record’s lines.</li>
          <li>For NetSuite you can also import a template as a JSON package from the template list.</li>
          <li>A value that is not printed on the document (such as a fixed location) is set as a fixed value, not as an AI hint.</li>
        </Steps>
        <Callout kind="tip">
          New ERP templates require review by default, so a request waits for a person before it can be posted.
        </Callout>
      </Section>

      <Section number={3} title="Reviewing and Posting a Request">
        <P>
          Open the request, check the header fields and the lines, fix anything wrong and approve. Only an Admin or the assigned
          approver can approve and post. When posting succeeds, the request shows a link that opens the record in the ERP. For Business Central,
          the source document is also attached to the record.
        </P>
      </Section>

      <Section number={4} title="When a Post Fails">
        <Bullets>
          <li>A failed post is rolled back so nothing half-finished is left in the ERP. Fix the data and retry.</li>
          <li>If the rollback itself fails, the request is marked as needing clean-up. An Admin removes the leftover record in Business Central and then clears the flag before it can be retried.</li>
          <li>If only the attachment failed, the record is still posted. Retry the attachment on its own.</li>
        </Bullets>
      </Section>

      <Section number={5} title="Connections per OpCo">
        <P>
          With OpCos switched on, each OpCo has its own NetSuite account and its own Business Central connection, set up by a Super Admin.
          An OpCo with no connection has no ERP access at all, it never borrows another OpCo’s. See “OpCos for Super Admins”.
        </P>
      </Section>
    </div>
  )
}
